import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import EditEmployeeModal from '../components/EditEmployeeModal';
import UnifiedAddEmployeeModal from '../components/UnifiedAddEmployeeModal';
import CreateTeamModal from '../components/CreateTeamModal';
import CreateRoleModal from '../components/CreateRoleModal';
import RolesTab from '../components/RolesTab';
import TeamsTab from '../components/TeamsTab';
import { EmployeesAnalyticsDrawer } from '../components/Analytics';
import api from '../utils/api';
import {
    UserPlus,
    Search,
    Edit,
    Trash2,
    Mail,
    Phone,
    Calendar,
    Briefcase,
    Users,
    Shield,
    BarChart3,
} from 'lucide-react';

const EmployeesViewAdmin = ({
    sidebarCollapsed,
    onMobileMenuClick,
    onAddEmployeeClick,
}) => {
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState('employees'); // 'employees', 'roles', 'teams'

    // Unified Add Employee Modal
    const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false);
    const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
    const [showCreateRoleModal, setShowCreateRoleModal] = useState(false);

    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [availableRoles, setAvailableRoles] = useState([]);
    const [companyData, setCompanyData] = useState(null);
    const [teamsRefreshKey, setTeamsRefreshKey] = useState(0);
    const [showAnalytics, setShowAnalytics] = useState(false);

    // Fetch company data
    const fetchCompanyData = async () => {
        try {
            const response = await api.get('/auth/profile');
            setCompanyData(response.data.company);
        } catch (error) {
            console.error('Error fetching company data:', error);
        }
    };

    // Fetch employees
    const fetchEmployees = async () => {
        try {
            setLoading(true);
            const response = await api.get('/employees');
            setEmployees(response.data.employees || []);
        } catch (error) {
            console.error('Error fetching employees:', error);
            toast.error('Failed to load employees');
        } finally {
            setLoading(false);
        }
    };

    // Fetch available roles
    const fetchRoles = async () => {
        try {
            const response = await api.get('/employees/roles');
            setAvailableRoles(response.data.roles || []);
        } catch (error) {
            console.error('Error fetching roles:', error);
        }
    };

    useEffect(() => {
        fetchCompanyData();
        fetchEmployees();
        fetchRoles();
    }, []);


    // Handler for all new user/employee types
    const handleCreateUser = () => {
        toast.success('User created successfully! 🎉', {
            duration: 4000,
        });
        fetchEmployees();
    };

    const handleTeamCreated = (team) => {
        toast.success(`Team "${team.team_name}" created! 🎉`);
        setTeamsRefreshKey(prev => prev + 1);
    };

    const handleRoleCreated = (roleName) => {
        toast.success(`Role "${roleName}" created! 🎉`);
        fetchRoles();
    };

    const handleDeleteEmployee = async (id) => {
        if (!window.confirm('Are you sure you want to delete this employee?')) {
            return;
        }

        try {
            await api.delete(`/employees/${id}`);
            toast.success('Employee deleted successfully');
            fetchEmployees();
        } catch (error) {
            toast.error('Failed to delete employee');
        }
    };

    const handleMarkOnboardingComplete = async (id) => {
        try {
            await api.post(`/employees/${id}/mark-onboarding-complete`);
            toast.success('Onboarding marked complete');
            fetchEmployees();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to mark onboarding complete');
        }
    };

    const handleEditEmployee = (employee) => {
        setSelectedEmployee(employee);
        setShowEditModal(true);
    };

    const handleEditSuccess = (updatedEmployee, emailChanged) => {
        if (emailChanged) {
            toast.success('Employee updated! Password reset email sent to new address. 📧', {
                duration: 5000,
            });
        } else {
            toast.success('Employee updated successfully! ✅');
        }
        fetchEmployees();
        setShowEditModal(false);
        setSelectedEmployee(null);
    };

    const filteredEmployees = employees.filter(
        (emp) =>
            emp.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            emp.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            emp.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            emp.position?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getStatusColor = (status) => {
        switch (status) {
            case 'active':
                return 'bg-emerald-100 text-emerald-700';
            case 'pending':
                return 'bg-amber-100 text-amber-700';
            case 'preboarding':
                return 'bg-purple-100 text-purple-700';
            case 'onboarding':
                return 'bg-blue-100 text-blue-700';
            case 'offboarding':
                return 'bg-orange-100 text-orange-700';
            case 'departed':
                return 'bg-muted text-muted-foreground';
            case 'inactive':
                return 'bg-muted text-muted-foreground';
            default:
                return 'bg-muted text-muted-foreground';
        }
    };

    return (
        <>



            {/* Main Content */}
            <main className="p-4 lg:p-6">
                {/* Header & Tabs */}
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-foreground mb-4">Organization</h1>
                    <div className="flex flex-col md:flex-row border-b border-border justify-between items-start md:items-center gap-4 md:gap-0 pb-4 md:pb-0">
                        <div className="flex overflow-x-auto whitespace-nowrap w-full md:w-auto no-scrollbar">
                            <button
                                onClick={() => setActiveTab('employees')}
                                className={`px-6 py-3 font-medium text-sm transition-all relative ${activeTab === 'employees'
                                    ? 'text-primary border-b-2 border-primary'
                                    : 'text-muted-foreground hover:text-foreground'
                                    }`}
                            >
                                <div className="flex items-center gap-2">
                                    <Users size={18} />
                                    Employees
                                </div>
                            </button>
                            <button
                                onClick={() => setActiveTab('roles')}
                                className={`px-6 py-3 font-medium text-sm transition-all relative ${activeTab === 'roles'
                                    ? 'text-primary border-b-2 border-primary'
                                    : 'text-muted-foreground hover:text-foreground'
                                    }`}
                            >
                                <div className="flex items-center gap-2">
                                    <Shield size={18} />
                                    Roles & Permissions
                                </div>
                            </button>
                            <button
                                onClick={() => setActiveTab('teams')}
                                className={`px-6 py-3 font-medium text-sm transition-all relative ${activeTab === 'teams'
                                    ? 'text-primary border-b-2 border-primary'
                                    : 'text-muted-foreground hover:text-foreground'
                                    }`}
                            >
                                <div className="flex items-center gap-2">
                                    <Briefcase size={18} />
                                    Teams
                                </div>
                            </button>
                        </div>

                        {/* Right Side Actions (Search, Buttons) */}
                        <div className="flex items-center gap-3 w-full md:w-auto pr-2">
                            <button
                                onClick={() => setShowAnalytics(true)}
                                className="px-4 py-2 bg-card border border-border text-foreground hover:bg-secondary rounded-xl font-medium transition-all text-sm flex items-center gap-2 whitespace-nowrap"
                            >
                                <BarChart3 size={16} />
                                View Analytics
                            </button>

                            {/* Search Bar - Only for Employees Tab */}
                            {activeTab === 'employees' && (
                                <div className="relative w-full md:w-64">
                                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                    <input
                                        type="text"
                                        placeholder="Search..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full pl-9 pr-4 py-2 text-sm border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                                    />
                                </div>
                            )}

                            {/* Desktop Create Team Button */}
                            {activeTab === 'teams' && (
                                <div className="hidden md:block">
                                    <button
                                        onClick={() => setShowCreateTeamModal(true)}
                                        className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-xl font-medium transition-all shadow-lg shadow-primary/20 text-sm flex items-center gap-2"
                                    >
                                        <Users size={16} />
                                        Create Team
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Tab Content */}
                {activeTab === 'employees' && (
                    <>
                        {/* Action Buttons Row */}
                        <div className="mb-6 flex flex-wrap gap-3">
                            <button
                                onClick={() => setShowAddEmployeeModal(true)}
                                className="px-4 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl font-semibold shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 transition-all inline-flex items-center gap-2 whitespace-nowrap text-sm"
                            >
                                <UserPlus size={18} />
                                Add Employee
                            </button>
                            <button
                                onClick={() => setShowCreateTeamModal(true)}
                                className="px-4 py-2.5 bg-secondary text-foreground hover:bg-muted rounded-xl font-semibold transition-all inline-flex items-center gap-2 whitespace-nowrap text-sm border border-border"
                            >
                                <Users size={18} />
                                Create Team
                            </button>
                            <button
                                onClick={() => setShowCreateRoleModal(true)}
                                className="px-4 py-2.5 bg-secondary text-foreground hover:bg-muted rounded-xl font-semibold transition-all inline-flex items-center gap-2 whitespace-nowrap text-sm border border-border"
                            >
                                <Briefcase size={18} />
                                Create Role
                            </button>
                        </div>

                        {/* Employees Table */}
                        <div className="bg-card rounded-2xl shadow-sm border border-border overflow-hidden">
                            {loading ? (
                                <div className="flex items-center justify-center py-12">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                                </div>
                            ) : filteredEmployees.length === 0 ? (
                                <div className="text-center py-12">
                                    <UserPlus size={48} className="mx-auto text-muted mb-4" />
                                    <h3 className="text-lg font-semibold text-foreground mb-2">
                                        No employees yet
                                    </h3>
                                    <p className="text-muted-foreground mb-6">
                                        Get started by adding your first employee
                                    </p>
                                    <button
                                        onClick={() => setShowAddEmployeeModal(true)}
                                        className="px-6 py-3 bg-primary hover:bg-primary/90 text-white rounded-xl font-semibold shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 transition-all inline-flex items-center gap-2"
                                    >
                                        <UserPlus size={20} />
                                        Add First Employee
                                    </button>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-secondary/50 border-b border-border">
                                            <tr>
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                                    Employee
                                                </th>
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                                    Contact
                                                </th>
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                                    Position
                                                </th>
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                                    Start Date
                                                </th>
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                                    Status
                                                </th>
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-border">
                                            {filteredEmployees.map((employee) => (
                                                <tr
                                                    key={employee.id}
                                                    className="hover:bg-secondary transition-colors"
                                                >
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-10 h-10 bg-primary/10 text-primary rounded-full flex items-center justify-center font-semibold">
                                                                {employee.first_name?.[0]}
                                                                {employee.last_name?.[0]}
                                                            </div>
                                                            <div>
                                                                <p className="font-semibold text-foreground">
                                                                    {employee.first_name} {employee.last_name}
                                                                </p>
                                                                {employee.manager_name && (
                                                                    <p className="text-xs text-muted-foreground">
                                                                        Manager: {employee.manager_name}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="space-y-1">
                                                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                                <Mail size={14} />
                                                                {employee.email}
                                                            </div>
                                                            {employee.phone && (
                                                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                                    <Phone size={14} />
                                                                    {employee.phone}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-2">
                                                            <Briefcase size={16} className="text-muted-foreground" />
                                                            <span className="text-sm text-foreground">
                                                                {employee.position || 'Not specified'}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-2">
                                                            <Calendar size={16} className="text-muted-foreground" />
                                                            <span className="text-sm text-foreground">
                                                                {employee.start_date
                                                                    ? new Date(employee.start_date).toLocaleDateString()
                                                                    : '-'}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span
                                                            className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                                                                employee.display_status || employee.lifecycle_stage
                                                            )}`}
                                                        >
                                                            {(employee.display_status || employee.lifecycle_stage)
                                                                ? (employee.display_status || employee.lifecycle_stage).charAt(0).toUpperCase() +
                                                                (employee.display_status || employee.lifecycle_stage).slice(1)
                                                                : 'Unknown'}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            {employee.lifecycle_stage === 'onboarding' && (
                                                                <button
                                                                    onClick={() => handleMarkOnboardingComplete(employee.id)}
                                                                    className="p-2 hover:bg-emerald-50 text-emerald-600 rounded-lg transition-colors whitespace-nowrap"
                                                                    title="Mark as Complete"
                                                                >
                                                                    Mark as Complete
                                                                </button>
                                                            )}
                                                            <button
                                                                onClick={() => handleEditEmployee(employee)}
                                                                className="p-2 hover:bg-primary/10 text-primary rounded-lg transition-colors"
                                                                title="Edit"
                                                            >
                                                                <Edit size={18} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteEmployee(employee.id)}
                                                                className="p-2 hover:bg-red-50 text-red-500 rounded-lg transition-colors"
                                                                title="Delete"
                                                            >
                                                                <Trash2 size={18} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        {/* Stats Cards */}
                        {employees.length > 0 && (
                            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mt-6">
                                <div className="bg-card rounded-xl p-4 border border-border">
                                    <p className="text-sm text-muted-foreground mb-1">Total</p>
                                    <p className="text-2xl font-bold text-foreground">{employees.length}</p>
                                </div>
                                <div className="bg-card rounded-xl p-4 border border-primary/20 bg-primary/5">
                                    <p className="text-sm text-muted-foreground mb-1">Active</p>
                                    <p className="text-2xl font-bold text-primary">
                                        {employees.filter((e) => e.lifecycle_stage === 'active').length}
                                    </p>
                                </div>
                                <div className="bg-card rounded-xl p-4 border border-purple-200 bg-purple-50/50">
                                    <p className="text-sm text-muted-foreground mb-1">Preboarding</p>
                                    <p className="text-2xl font-bold text-purple-500">
                                        {employees.filter((e) => e.lifecycle_stage === 'preboarding').length}
                                    </p>
                                </div>
                                <div className="bg-card rounded-xl p-4 border border-blue-200 bg-blue-50/50">
                                    <p className="text-sm text-muted-foreground mb-1">Onboarding</p>
                                    <p className="text-2xl font-bold text-blue-500">
                                        {employees.filter((e) => e.lifecycle_stage === 'onboarding').length}
                                    </p>
                                </div>
                                <div className="bg-card rounded-xl p-4 border border-emerald-200 bg-emerald-50/50">
                                    <p className="text-sm text-muted-foreground mb-1">Completed</p>
                                    <p className="text-2xl font-bold text-emerald-500">
                                        {employees.filter((e) => (e.display_status || e.lifecycle_stage) === 'active' && e.onboarding_complete_date).length}
                                    </p>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {activeTab === 'roles' && <RolesTab />}

                {activeTab === 'teams' && <TeamsTab key={teamsRefreshKey} />}
            </main>



            {/* Edit Employee Modal */}
            <EditEmployeeModal
                isOpen={showEditModal}
                onClose={() => {
                    setShowEditModal(false);
                    setSelectedEmployee(null);
                }}
                onSuccess={handleEditSuccess}
                employee={selectedEmployee}
            />

            {/* Unified Add Employee Modal */}
            <UnifiedAddEmployeeModal
                isOpen={showAddEmployeeModal}
                onClose={() => setShowAddEmployeeModal(false)}
                onSuccess={handleCreateUser}
                companyData={companyData}
            />

            {/* Create Team Modal */}
            <CreateTeamModal
                isOpen={showCreateTeamModal}
                onClose={() => setShowCreateTeamModal(false)}
                onSuccess={handleTeamCreated}
            />

            {/* Create Role Modal */}
            <CreateRoleModal
                isOpen={showCreateRoleModal}
                onClose={() => setShowCreateRoleModal(false)}
                onSuccess={handleRoleCreated}
            />

            {/* Analytics Drawer */}
            <EmployeesAnalyticsDrawer
                isOpen={showAnalytics}
                onClose={() => setShowAnalytics(false)}
            />
        </>
    );
};

export default EmployeesViewAdmin;